import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    ResponsiveDialog,
    RichTextArea,
    RubricTable,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { BASELINE_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

const defaultForm = {
    assignment: '',
    criteria: {},
};

const defaultErrors = {
    assignment: '',
    criteria: {},
};

const EditBaselineDialog = memo(({ open, rubric, baseline, onClose, onSave }) => {
    const { t } = useTranslation('assignment');
    const { setDialog, closeDialog } = useDialog();
    const { device } = useViewport();

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const handleClose = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                onClose();
                closeDialog();
                setForm(baseline || defaultForm);
                setErrors(defaultErrors);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateAssignment = useCallback(() => {
        let error = '';
        if (!getHTMLWordCount(form.assignment.trim()))
            error = t('error.empty-assignment', 'Assignment cannot be empty');
        if (getHTMLWordCount(form.assignment.trim()) > BASELINE_WORD_COUNT_LIMIT)
            error = t('error.assignment-too-long', 'Assignment is too long');
        setErrors((current) => {
            return { ...current, assignment: error };
        });
        return !Boolean(error);
    }, [form.assignment, t]);

    const validateCriteria = useCallback(() => {
        if (!rubric) return false;
        let error = false;
        rubric.criteria.forEach((criterion) => {
            if (Boolean(form.criteria[criterion.id])) {
                setErrors((current) => ({ ...current, criteria: omit(current.criteria, [criterion.id]) }));
            } else {
                error = true;
                setErrors((current) => ({ ...current, criteria: { ...current.criteria, [criterion.id]: true } }));
            }
        });
        return !error;
    }, [rubric, form.criteria]);

    const validateAll = () => {
        const isAssignmentValid = validateAssignment();
        const areCriteriaValid = validateCriteria();
        if (isAssignmentValid && areCriteriaValid) return true;
        return false;
    };

    const handleChange = (criterionId, optionId) => {
        setForm((current) => ({ ...current, criteria: { ...current.criteria, [criterionId]: optionId } }));
    };

    const handleClickSave = () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            onSave(form);
            onClose();
        }
    };

    useEffect(() => {
        if (hasClickedSubmit) validateAssignment();
    }, [hasClickedSubmit, validateAssignment]);

    useEffect(() => {
        if (hasClickedSubmit) validateCriteria();
    }, [hasClickedSubmit, validateCriteria]);

    useEffect(() => {
        if (open && baseline) {
            setForm(baseline);
        }
    }, [open, baseline]);

    useEffect(() => {
        if (!rubric) {
            setForm(defaultForm);
            setErrors(defaultErrors);
            setHasClickedSubmit(false);
        }
    }, [rubric]);

    useEffect(() => {
        if (!baseline) {
            setForm(defaultForm);
            setErrors(defaultErrors);
            setHasClickedSubmit(false);
        }
    }, [baseline]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('dialog.edit-baseline.title', 'Assignment Baseline')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent device={device}>
                <CustomStack
                    direction={device === 'desktop' ? 'row' : 'column'}
                    device={device}
                    spacing={device === 'desktop' ? 0 : 5}
                >
                    <InputStack device={device} spacing={5}>
                        <Text>
                            <Trans
                                i18nKey='dialog.edit-baseline.description'
                                components={{
                                    'semi-bold': <Text component={'span'} weight={'semibold'} />,
                                }}
                            >
                                Please provide an example of a past <semi-bold>high-scoring</semi-bold> assignment with
                                the grades it received. This helps the AI to establish a baseline on the rubric based on
                                your assessment practices.
                            </Trans>
                        </Text>
                        <CustomRichTextArea
                            device={device}
                            error={Boolean(errors.assignment)}
                            helperText={`${getHTMLWordCount(form.assignment)}/${BASELINE_WORD_COUNT_LIMIT} words`}
                            helperTextPosition={'end'}
                            placeholder={t(
                                'dialog.edit-baseline.assignment-placeholder',
                                'Please paste the assignment here'
                            )}
                            value={form.assignment}
                            onChange={(value) => setForm((current) => ({ ...current, assignment: value }))}
                        />
                    </InputStack>
                    <CustomPaper device={device}>
                        <RubricTable
                            type={'submit'}
                            data={rubric?.criteria}
                            errors={errors.criteria}
                            form={form.criteria}
                            onChange={handleChange}
                        />
                    </CustomPaper>
                </CustomStack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={handleClickSave}>{t('common:controls.save', 'Save')}</Button>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ device, theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
        ...(device === 'desktop' && {
            padding: 0,
        }),
    };
});

const CustomStack = styled(Stack)(({ device }) => {
    return {
        ...(device === 'desktop' && {
            height: '100%',
            '& > *': {
                flex: 1,
            },
        }),
    };
});

const InputStack = styled(Stack)(({ device, theme }) => {
    return {
        ...(device === 'desktop' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomRichTextArea = styled(RichTextArea)(({ device }) => {
    return {
        ...(device === 'desktop'
            ? {
                  '& .MuiRichTextArea-root': {
                      minHeight: 'calc(100vh - 318px)',
                      maxHeight: 'calc(100vh - 318px)',
                  },
              }
            : {
                  '& .MuiRichTextArea-root': {
                      minHeight: '20rem',
                      maxHeight: '30rem',
                  },
              }),
    };
});

const CustomPaper = styled(Paper)(({ device, theme }) => {
    return {
        padding: theme.spacing(6),
        ...(device === 'desktop'
            ? {
                  borderRadius: '0',
                  overflow: 'auto',
              }
            : {
                  borderRadius: '12px 12px 0 0',
                  margin: theme.spacing(0, -6, -5),
              }),
    };
});

export default EditBaselineDialog;
